import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@scio3/ui';
import { SUBMIT_USER_SURVEY, type SubmitUserSurveyVariables } from '@scio3/graphql-client';
import { useBillingDialogStore } from '../stores/billing-dialog-store';

type ReasonOption = { value: string; labelKey: string };

const REASON_OPTIONS: ReasonOption[] = [
  { value: 'too_expensive', labelKey: "It's too expensive" },
  { value: 'understanding_product', labelKey: "I'm having too many technical issues" },
  { value: 'switched_solution', labelKey: 'I switched to another solution' },
  { value: 'missing_features', labelKey: 'A feature I need is missing' },
  { value: 'no_longer_need', labelKey: 'I no longer need digital signage' },
  { value: 'temporary_seasonal', labelKey: 'I only need it temporarily or seasonally' },
  { value: 'other', labelKey: 'Other' },
];

/**
 * "Why are you downgrading?" reason survey shown BEFORE the feature-comparison
 * overlay when the user picks Free in the subscription wizard. Captures a reason
 * + free-text note and persists them to the userSurveys collection via the
 * submitUserSurvey mutation (formType 'downgradeSubscription').
 *
 * Flow:
 *  - "Keep current plan" → close overlay, leave wizard plan-step intact
 *  - "Downgrade to free" → close survey, open DowngradeComparisonDialog
 */
export function DowngradeSurveyDialog() {
  const { t } = useTranslation();
  const open = useBillingDialogStore((s) => s.downgradeSurveyOpen);
  const ctx = useBillingDialogStore((s) => s.downgradeSurveyContext);
  const close = useBillingDialogStore((s) => s.closeDowngradeSurvey);
  const openDowngradeComparison = useBillingDialogStore((s) => s.openDowngradeComparison);
  const [submitSurvey] = useMutation<unknown, SubmitUserSurveyVariables>(SUBMIT_USER_SURVEY);

  const subAccountId = ctx.subAccountId as string | undefined;

  const [reason, setReason] = useState<string>('');
  const [additionalInfo, setAdditionalInfo] = useState<string>('');
  // Surfaced when the user clicks "Downgrade to free" without picking a reason.
  // Cleared as soon as they select one (see handleReasonChange).
  const [missingReasonError, setMissingReasonError] = useState(false);

  useEffect(() => {
    if (open) {
      setReason('');
      setAdditionalInfo('');
      setMissingReasonError(false);
    }
  }, [open]);

  /** Wrap setReason so picking any option clears the "missing reason" error. */
  const handleReasonChange = (next: string) => {
    setReason(next);
    if (next) setMissingReasonError(false);
  };

  const handleKeep = () => {
    close();
  };

  const handleDowngrade = () => {
    if (!reason) {
      setMissingReasonError(true);
      return;
    }

    // Persist the downgrade survey to the userSurveys collection. Fire-and-
    // forget — a survey-save failure must never block the downgrade flow.
    const selected = REASON_OPTIONS.find((r) => r.value === reason);
    const questions: SubmitUserSurveyVariables['input']['questions'] = [
      {
        key: 'downgrade_reason',
        text: 'Why are you downgrading?',
        answer: reason,
        answerLabel: selected?.labelKey ?? '',
      },
    ];
    const note = additionalInfo.trim();
    if (note) {
      questions.push({
        key: 'additional_info',
        text: 'Tell us more about your decision',
        answer: note,
        answerLabel: note,
      });
    }
    void submitSurvey({
      variables: {
        input: {
          formId: 'downgradeSubscription',
          formVersion: 1,
          formType: 'downgradeSubscription',
          questions,
        },
      },
    }).catch((err) => {
      console.error('submitUserSurvey (downgrade) failed', err);
    });

    close();
    openDowngradeComparison({ subAccountId });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent
        className="flex max-h-[85vh] max-w-lg flex-col overflow-hidden"
        data-testid="downgrade-survey-dialog"
      >
        <div className="space-y-1.5 pr-8 pt-4">
          <DialogTitle>{t('Why are you downgrading?')}</DialogTitle>
          <DialogDescription>
            {t('We use this information to improve OptiSigns.')}
          </DialogDescription>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-1 pb-1 pt-4">
          {missingReasonError && (
            <p
              role="alert"
              className="text-sm font-medium text-destructive"
              data-testid="downgrade-survey-missing-reason-error"
            >
              {t('Please select a reason before downgrading.')}
            </p>
          )}
          <RadioGroup value={reason} onValueChange={handleReasonChange} className="gap-1">
            {REASON_OPTIONS.map((opt) => {
              const id = `downgrade-reason-${opt.value}`;
              const isSelected = reason === opt.value;
              return (
                <Label
                  key={opt.value}
                  htmlFor={id}
                  data-state={isSelected ? 'checked' : 'unchecked'}
                  className={cn(
                    'flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm font-normal transition-colors hover:bg-accent',
                    isSelected && 'bg-secondary hover:bg-secondary',
                  )}
                >
                  <RadioGroupItem
                    value={opt.value}
                    id={id}
                    data-testid={`downgrade-reason-${opt.value}`}
                    className="h-5 w-5 border-2 border-input shadow-none data-[state=checked]:border-[5px] data-[state=checked]:border-primary [&_svg]:hidden"
                  />
                  <span>{t(opt.labelKey)}</span>
                </Label>
              );
            })}
          </RadioGroup>

          <div className="space-y-2">
            <Label htmlFor="downgrade-additional-info" className="sr-only">
              {t('Tell us more about your decision')}
            </Label>
            <Input
              id="downgrade-additional-info"
              data-testid="downgrade-additional-info-input"
              placeholder={`${t('Tell us more about your decision')}...`}
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-6">
          <Button
            variant="outline"
            onClick={handleDowngrade}
            data-testid="downgrade-survey-downgrade-btn"
          >
            {t('Downgrade to free')}
          </Button>
          <Button onClick={handleKeep} data-testid="downgrade-survey-keep-btn">
            {t('Choose a different plan')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
